<div class="camera-demo-container p-4 max-w-6xl mx-auto">
    <!-- Header -->
    <div class="text-center mb-8">
        <div class="mb-6">
            <i class="pi pi-camera text-6xl text-blue-600 mb-4"></i>
        </div>
        <h1 class="text-4xl font-bold text-gray-800 mb-4">Camera Scanner Demo</h1>
        <p class="text-xl text-gray-600 mb-6">
            Experience the power of our integrated camera scanner with Laava API
        </p>

        <div class="flex justify-center gap-4 flex-wrap">
            <p-button [icon]="showScanner ? 'pi pi-times' : 'pi pi-camera'"
                [label]="showScanner ? 'Close Scanner' : 'Open Scanner'" severity="primary" size="large"
                (onClick)="toggleScanner()">
            </p-button>

            <p-button icon="pi pi-external-link" label="View Documentation" severity="secondary" size="large"
                routerLink="/scanner">
            </p-button>

            <p-button icon="pi pi-info-circle" label="API Info" severity="help" size="large" (onClick)="showApiInfo()">
            </p-button>
        </div>
    </div>

    <!-- Feature Overview -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <p-card class="hover:shadow-lg transition-shadow duration-300">
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-6 text-center">
                    <i class="pi pi-video text-4xl text-blue-600 mb-2"></i>
                    <h3 class="text-lg font-semibold text-blue-800">Camera Integration</h3>
                </div>
            </ng-template>
            <div class="p-4">
                <p class="text-gray-600 text-center">
                    Seamless camera access with automatic permission management and high-quality image capture
                </p>
                <div class="mt-4 flex justify-center">
                    <p-tag value="Real-time" severity="info" icon="pi pi-clock"></p-tag>
                </div>
            </div>
        </p-card>

        <p-card class="hover:shadow-lg transition-shadow duration-300">
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-br from-green-50 to-green-100 p-6 text-center">
                    <i class="pi pi-map-marker text-4xl text-green-600 mb-2"></i>
                    <h3 class="text-lg font-semibold text-green-800">Location Services</h3>
                </div>
            </ng-template>
            <div class="p-4">
                <p class="text-gray-600 text-center">
                    Enhanced scanning with precise geolocation data for better API results
                </p>
                <div class="mt-4 flex justify-center">
                    <p-tag value="GPS Enabled" severity="success" icon="pi pi-map"></p-tag>
                </div>
            </div>
        </p-card>

        <p-card class="hover:shadow-lg transition-shadow duration-300">
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-br from-purple-50 to-purple-100 p-6 text-center">
                    <i class="pi pi-cloud-upload text-4xl text-purple-600 mb-2"></i>
                    <h3 class="text-lg font-semibold text-purple-800">Laava API</h3>
                </div>
            </ng-template>
            <div class="p-4">
                <p class="text-gray-600 text-center">
                    Direct integration with Laava's powerful scanning API for instant results
                </p>
                <div class="mt-4 flex justify-center">
                    <p-tag value="Cloud API" severity="warn" icon="pi pi-cloud"></p-tag>
                </div>
            </div>
        </p-card>
    </div>

    <!-- Scanner Component -->
    <div *ngIf="showScanner" class="mb-8">
        <p-card>
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-4">
                    <h2 class="text-xl font-semibold text-white">Camera Scanner</h2>
                </div>
            </ng-template>

            <app-camera-scanner></app-camera-scanner>
        </p-card>
    </div>

    <!-- Technical Details -->
    <p-card class="mb-8">
        <ng-template pTemplate="header">
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 p-6">
                <div class="flex items-center gap-3">
                    <i class="pi pi-cog text-2xl text-gray-600"></i>
                    <h2 class="text-xl font-semibold text-gray-800">Technical Specifications</h2>
                </div>
            </div>
        </ng-template>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
            <div>
                <div class="flex items-center gap-2 mb-4">
                    <i class="pi pi-globe text-lg text-blue-600"></i>
                    <h3 class="text-lg font-semibold text-gray-800">Browser Requirements</h3>
                </div>
                <div class="space-y-3">
                    <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <i class="pi pi-check-circle text-green-600"></i>
                        <span class="text-gray-700">HTTPS connection required</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <i class="pi pi-check-circle text-green-600"></i>
                        <span class="text-gray-700">Camera API support</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <i class="pi pi-check-circle text-green-600"></i>
                        <span class="text-gray-700">Geolocation API support</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <i class="pi pi-check-circle text-green-600"></i>
                        <span class="text-gray-700">Modern browser (Chrome 53+, Firefox 36+, Safari 11+)</span>
                    </div>
                </div>
            </div>

            <div>
                <div class="flex items-center gap-2 mb-4">
                    <i class="pi pi-cloud text-lg text-purple-600"></i>
                    <h3 class="text-lg font-semibold text-gray-800">API Features</h3>
                </div>
                <div class="space-y-3">
                    <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <i class="pi pi-bolt text-blue-600"></i>
                        <span class="text-gray-700">Real-time image processing</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <i class="pi pi-map-marker text-blue-600"></i>
                        <span class="text-gray-700">Location data integration</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <i class="pi pi-shield text-blue-600"></i>
                        <span class="text-gray-700">Error handling & validation</span>
                    </div>
                    <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <i class="pi pi-clock text-blue-600"></i>
                        <span class="text-gray-700">Rate limiting support</span>
                    </div>
                </div>
            </div>
        </div>
    </p-card>

    <!-- Getting Started -->
    <p-card>
        <ng-template pTemplate="header">
            <div class="bg-gradient-to-r from-green-50 to-green-100 p-6">
                <div class="flex items-center gap-3">
                    <i class="pi pi-play-circle text-2xl text-green-600"></i>
                    <h2 class="text-xl font-semibold text-green-800">Getting Started</h2>
                </div>
            </div>
        </ng-template>

        <div class="p-6">
            <div class="space-y-6">
                <div class="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                    <div
                        class="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        <i class="pi pi-key"></i>
                    </div>
                    <div class="flex-1">
                        <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i class="pi pi-external-link text-blue-600"></i>
                            Get Your API Key
                        </h3>
                        <p class="text-gray-600">
                            Visit the <a href="https://developers.laava.id/docs/api-resources/scan" target="_blank"
                                class="text-blue-600 hover:text-blue-800 underline font-medium">Laava Developer
                                Portal</a>
                            to obtain your API key for scanning operations.
                        </p>
                    </div>
                </div>

                <div class="flex items-start gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
                    <div
                        class="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        <i class="pi pi-shield"></i>
                    </div>
                    <div class="flex-1">
                        <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i class="pi pi-cog text-orange-600"></i>
                            Configure Permissions
                        </h3>
                        <p class="text-gray-600">
                            Grant camera and location permissions when prompted. These are required for optimal scanning
                            performance.
                        </p>
                    </div>
                </div>

                <div class="flex items-start gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                    <div
                        class="bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        <i class="pi pi-camera"></i>
                    </div>
                    <div class="flex-1">
                        <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i class="pi pi-play text-green-600"></i>
                            Start Scanning
                        </h3>
                        <p class="text-gray-600">
                            Click the "Open Scanner" button above to activate the camera scanner and begin capturing
                            images.
                        </p>
                    </div>
                </div>

                <div class="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
                    <div
                        class="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        <i class="pi pi-cloud-upload"></i>
                    </div>
                    <div class="flex-1">
                        <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i class="pi pi-send text-purple-600"></i>
                            Send to API
                        </h3>
                        <p class="text-gray-600">
                            Capture your image and send it directly to the Laava API for instant scanning results.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </p-card>
</div>